import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Download, ExternalLink, PenLine, Send, Undo2, XCircle } from 'lucide-react';
import { api } from '../../services/api';
import Badge from '../ui/Badge';
import Dialog from '../ui/Dialog';
import Field, { FormServerError } from '../ui/Field';
import Tabs from '../ui/Tabs';
import StateBlock from '../ui/StateBlock';
import { formatDateTime } from '../../utils/format';
import { canShowPdfInline, reviewTone, trackSteps } from '../../utils/documentReviewModel';

/** The tracking row (טיוטה ← אצל מנהל OTE ← נשלח ← נחתם). */
export function ReviewTrack({ state, transcribed }: { state: string; transcribed?: boolean | null }) {
  const { t } = useTranslation();
  const steps = trackSteps(state, { transcribed });
  return (
    <ol className="flex flex-wrap items-center gap-1.5 text-xs" aria-label={t('docReview.track.label')}>
      {steps.map((s, i) => (
        <li key={s.key} className="flex items-center gap-1.5">
          {i > 0 && <span className="text-surface-400" aria-hidden="true">←</span>}
          <Badge tone={s.done ? 'success' : s.current ? 'warning' : 'neutral'} dot={s.current}>
            {t(`docReview.track.${s.key}`)}
          </Badge>
        </li>
      ))}
    </ol>
  );
}

export function ReviewStateBadge({ state }: { state: string }) {
  const { t } = useTranslation();
  return <Badge tone={reviewTone(state)} dot>{t(`docReview.state.${state}`)}</Badge>;
}

function PdfFrame({ docId, version }: { docId: string; version: string }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let alive = true;
    let made: string | null = null;
    setError(false);
    api.getDocumentReviewPdf(docId)
      .then((blob) => { if (!alive) return; made = URL.createObjectURL(blob); setUrl(made); })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; if (made) URL.revokeObjectURL(made); };
  }, [docId, version]);
  if (error) return <StateBlock kind="error" size="panel" title={t('docReview.pdfError')} />;
  if (!url) return <div className="h-96 animate-pulse rounded-lg bg-surface-100" />;
  return (
    <div className="space-y-2">
      {/* FIX-D337-2 §1: no inline PDF viewer (phone) -> a note instead of an empty frame; the buttons always stay. */}
      {canShowPdfInline()
        ? <iframe title={t('docReview.pdfTitle')} src={url} className="h-[70vh] w-full rounded-lg border border-surface-200 bg-surface-0" />
        : <p className="rounded-lg border border-surface-200 px-4 py-3 text-sm text-surface-700">{t('docReview.noInlinePdf')}</p>}
      <div className="flex flex-wrap gap-2">
        <a className="btn btn-secondary btn-sm" href={url} target="_blank" rel="noopener noreferrer">
          <ExternalLink size={16} aria-hidden="true" /> {t('docReview.openPdf')}
        </a>
        <a className="btn btn-secondary btn-sm" href={url} download>
          <Download size={16} aria-hidden="true" /> {t('docReview.download')}
        </a>
      </div>
    </div>
  );
}

type TabKey = 'doc' | 'history' | 'text';

/**
 * FIX-D337 stage א - one review document: the PDF, "היסטוריה", the text (a template document), and the actions:
 * the OTE manager returns with a note / signs and sends; the lead manager reads and comments.
 */
export default function ReviewDetail({ docId, onChanged }: { docId: string; onChanged?: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [tab, setTab] = useState<TabKey>('doc');
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnNote, setReturnNote] = useState('');
  const [comment, setComment] = useState('');
  const [text, setText] = useState<string | null>(null);
  const [to, setTo] = useState<{ email: string; name: string } | null>(null);
  const query = useQuery({ queryKey: ['document-review', docId], queryFn: async () => (await api.getDocumentReview(docId)).data });
  const doc = query.data;
  useEffect(() => { setTab('doc'); setText(null); setTo(null); }, [docId]);
  useEffect(() => {
    if (doc && to === null) setTo({ email: doc.recipient_email ?? '', name: doc.recipient_name ?? '' });
  }, [doc, to]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['document-review', docId] });
    qc.invalidateQueries({ queryKey: ['document-reviews'] });
    qc.invalidateQueries({ queryKey: ['document-review-count'] });
    onChanged?.();
  };
  const sign = useMutation({ mutationFn: () => api.signAndSendDocument(docId, { recipient_email: to?.email || null, recipient_name: to?.name || null }), onSuccess: refresh });
  const ret = useMutation({ mutationFn: () => api.returnDocumentReview(docId, returnNote), onSuccess: () => { setReturnOpen(false); setReturnNote(''); refresh(); } });
  const cmt = useMutation({ mutationFn: () => api.commentDocumentReview(docId, comment), onSuccess: () => { setComment(''); refresh(); } });
  const saveText = useMutation({ mutationFn: () => api.updateDocumentReviewText(docId, text ?? ''), onSuccess: () => { setText(null); refresh(); } });
  const resubmit = useMutation({ mutationFn: () => (doc?.quote_id ? api.submitQuoteForReview(doc.quote_id) : api.submitDocumentForReview(docId)), onSuccess: refresh });
  const cancel = useMutation({ mutationFn: () => api.voidDocumentReview(docId), onSuccess: refresh });

  if (query.isLoading) return <div className="h-64 animate-pulse rounded-xl bg-surface-100" />;
  if (query.isError || !doc) return <StateBlock kind="error" size="panel" title={t('docReview.loadError')} />;

  const tabs = [
    { key: 'doc' as const, label: t('docReview.tab.doc') },
    { key: 'history' as const, label: t('docReview.tab.history') },
    ...(doc.source_kind === 'template' ? [{ key: 'text' as const, label: t('docReview.tab.text') }] : []),
  ];
  const version = `${doc.review_state}:${doc.updated_at}`;
  const canVoid = doc.is_ote_manager ? ['with_ote', 'returned', 'sent'].includes(doc.review_state) : ['with_ote', 'returned'].includes(doc.review_state);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-surface-900">{doc.title}</h2>
          <p className="mt-0.5 text-xs text-surface-600">
            {[doc.lead_name || doc.organization_name, doc.submitted_by_name && t('docReview.submittedBy', { name: doc.submitted_by_name }),
              doc.submitted_at && formatDateTime(doc.submitted_at)].filter(Boolean).join(' · ')}
          </p>
        </div>
        <ReviewStateBadge state={doc.review_state} />
      </div>
      <ReviewTrack state={doc.review_state} />
      {doc.sample_content && (
        <p className="flex items-center gap-2 rounded-lg bg-warning-50 px-3 py-2 text-xs text-warning-800">
          <AlertTriangle size={14} aria-hidden="true" /> {t('docReview.sampleContent')}
        </p>
      )}
      {doc.review_state === 'returned' && doc.return_note && (
        <div className="rounded-lg bg-danger-50 px-3 py-2 text-sm text-danger-700">
          <strong>{t('docReview.returnNote')}:</strong> {doc.return_note}
        </div>
      )}

      {(doc.transcripts ?? []).length > 0 && (
        <p className="flex flex-wrap items-center gap-2 text-xs text-surface-600">
          <span>{t('docReview.transcripts')}:</span>
          {doc.transcripts.map((tr: any) => (
            <Link key={tr.id} className="link" to={`/documents?doc=${tr.id}`}>{tr.title}</Link>
          ))}
        </p>
      )}
      {(doc.package_documents ?? []).length > 0 && (
        <div className="rounded-lg border border-surface-200 px-3 py-2">
          <p className="mb-1 text-xs font-medium text-surface-700">{t('charPackage.packageTitle')}</p>
          <ul className="space-y-1 text-xs">
            {doc.package_documents.map((d: any) => (
              <li key={d.id} className="flex flex-wrap items-center justify-between gap-2">
                <Link className="link" to={`/documents?doc=${d.id}`}>{d.title}</Link>
                <span className="text-surface-600">{d.customer_facing ? t('charPackage.toCustomer') : t('charPackage.atDelivery')}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <Tabs items={tabs} value={tab} onChange={(k) => setTab(k as TabKey)} ariaLabel={t('docReview.tabsLabel')} />
      {tab === 'doc' && <PdfFrame docId={docId} version={version} />}
      {tab === 'history' && (
        <ol className="space-y-2">
          {(doc.events ?? []).map((e: any) => (
            <li key={e.id} className="rounded-lg border border-surface-200 px-3 py-2 text-sm">
              <div className="flex flex-wrap justify-between gap-2">
                <span className="font-medium text-surface-900">{t(`docReview.event.${e.event_type}`)}</span>
                <span className="text-xs text-surface-600">{[e.actor_name, formatDateTime(e.created_at)].filter(Boolean).join(' · ')}</span>
              </div>
              {e.note && <p className="mt-1 whitespace-pre-wrap text-surface-700">{e.note}</p>}
            </li>
          ))}
        </ol>
      )}
      {tab === 'text' && (
        <div className="space-y-2">
          <textarea
            className="input min-h-[50vh] w-full font-mono text-sm"
            aria-label={t('docReview.tab.text')}
            value={text ?? doc.body_text ?? ''}
            readOnly={!doc.can_edit_text}
            onChange={(e) => setText(e.target.value)}
          />
          {doc.can_edit_text && (
            <button type="button" className="btn btn-primary btn-sm" disabled={text === null || saveText.isPending} onClick={() => saveText.mutate()}>
              {t('docReview.saveText')}
            </button>
          )}
          <FormServerError error={saveText.error} fallback={t('docReview.actionError')} />
        </div>
      )}

      {doc.can_sign && (
        <section className="space-y-3 rounded-xl border border-surface-200 p-4" aria-label={t('docReview.signSection')}>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('docReview.recipientEmail')} id="rv-to-email" required>
              <input id="rv-to-email" className="input" type="email" dir="ltr" value={to?.email ?? ''} onChange={(e) => setTo({ email: e.target.value, name: to?.name ?? '' })} />
            </Field>
            <Field label={t('docReview.recipientName')} id="rv-to-name" optional>
              <input id="rv-to-name" className="input" value={to?.name ?? ''} onChange={(e) => setTo({ email: to?.email ?? '', name: e.target.value })} />
            </Field>
          </div>
          {!doc.has_signature && (
            <p className="text-sm text-warning-800">
              {t('docReview.noSignature')} <Link className="link" to="/profile#my-signature">{t('docReview.toProfile')}</Link>
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn btn-primary" disabled={!doc.has_signature || sign.isPending || !to?.email} onClick={() => sign.mutate()}>
              <Send size={16} aria-hidden="true" /> {t('docReview.signAndSend')}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setReturnOpen(true)}>
              <Undo2 size={16} aria-hidden="true" /> {t('docReview.returnWithNote')}
            </button>
            {doc.source_kind === 'template' && (
              <button type="button" className="btn btn-secondary" onClick={() => setTab('text')}>
                <PenLine size={16} aria-hidden="true" /> {t('docReview.editText')}
              </button>
            )}
            {doc.quote_id && (
              <Link className="btn btn-secondary" to={`/quotes/${doc.quote_id}`}><PenLine size={16} aria-hidden="true" /> {t('docReview.openQuote')}</Link>
            )}
          </div>
          <FormServerError error={sign.error} fallback={t('docReview.actionError')} />
        </section>
      )}

      {!doc.is_ote_manager && ['draft', 'returned'].includes(doc.review_state) && (
        <button type="button" className="btn btn-primary btn-sm" disabled={resubmit.isPending} onClick={() => resubmit.mutate()}>
          <Send size={16} aria-hidden="true" /> {t('docReview.submitAgain')}
        </button>
      )}

      <div className="space-y-2">
        <Field label={t('docReview.comment')} id="rv-comment" optional>
          <textarea id="rv-comment" className="input min-h-[4rem] w-full" value={comment} onChange={(e) => setComment(e.target.value)} />
        </Field>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn btn-secondary btn-sm" disabled={!comment.trim() || cmt.isPending} onClick={() => cmt.mutate()}>
            {t('docReview.addComment')}
          </button>
          {canVoid && (
            <button type="button" className="btn btn-ghost btn-sm text-danger-700" disabled={cancel.isPending} onClick={() => cancel.mutate()}>
              <XCircle size={16} aria-hidden="true" /> {t('docReview.void')}
            </button>
          )}
        </div>
        <FormServerError error={cmt.error || resubmit.error || cancel.error || ret.error} fallback={t('docReview.actionError')} />
      </div>

      <Dialog
        open={returnOpen}
        onClose={() => setReturnOpen(false)}
        title={t('docReview.returnWithNote')}
        footer={(
          <div className="flex gap-2">
            <button type="button" className="btn btn-primary" disabled={!returnNote.trim() || ret.isPending} onClick={() => ret.mutate()}>{t('docReview.return')}</button>
            <button type="button" className="btn btn-secondary" onClick={() => setReturnOpen(false)}>{t('common.cancel')}</button>
          </div>
        )}
      >
        <Field label={t('docReview.returnNote')} id="rv-return-note" required>
          <textarea id="rv-return-note" className="input min-h-[6rem] w-full" value={returnNote} onChange={(e) => setReturnNote(e.target.value)} />
        </Field>
      </Dialog>
    </div>
  );
}
